import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSpinner, faWandMagicSparkles, faPaperPlane, faRotateLeft, faQuestionCircle, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { ConsultationService, sharedApiClient } from '../fetcher';
import '@medprompt/shared/src/common/modals.css';
import '../detail/tabs/MessagingModals.css';
import './NotesTemplateEditorModal.css';

const TITLE_MAX_LENGTH = 100;
const CONTENT_PLACEHOLDER = 'Define your template structure here. Use placeholders like [Presenting complaint] for sections the AI should fill in...';

const TemplateContentField = ({ value, onChange, readOnly, placeholder, hasError, flush }) => {
  const [isEditing, setIsEditing] = useState(!readOnly);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (readOnly) {
      setIsEditing(false);
    }
  }, [readOnly]);

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isEditing]);

  const errorClass = hasError ? ' has-error' : '';
  const flushClass = flush ? ' flush' : '';

  if (isEditing && !readOnly) {
    return (
      <textarea
        ref={textareaRef}
        className={`notes-template-content-textarea${errorClass}`}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        onBlur={() => {
          setTimeout(() => setIsEditing(false), 0);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.target.blur();
          }
        }}
        placeholder={placeholder}
      />
    );
  }

  return (
    <div
      className={`notes-template-content-text${readOnly ? '' : ' notes-template-content-editable'}${errorClass}${flushClass}`}
      onClick={() => {
        if (!readOnly) setIsEditing(true);
      }}
      title={!readOnly ? 'Click to edit' : ''}
      onMouseMove={(e) => {
        const tooltip = e.currentTarget.querySelector('.edit-tooltip');
        if (tooltip) {
          tooltip.style.left = `${e.clientX + 10}px`;
          tooltip.style.top = `${e.clientY + 10}px`;
        }
      }}
    >
      {value || <em style={{ color: '#999' }}>{placeholder || 'No content'}</em>}
      {!readOnly && <div className="edit-tooltip">Click to edit template</div>}
    </div>
  );
};

const NotesTemplateEditorModal = ({ template, onSave, onClose }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [isInitialized, setIsInitialized] = useState(false);

  // Chat history state
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [pendingContent, setPendingContent] = useState(null);
  const [pendingMessage, setPendingMessage] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const chatEndRef = useRef(null);

  const consultationService = useMemo(() => new ConsultationService(sharedApiClient), []);

  const isEditMode = template !== null;
  const storageKey = 'medprompt_notes_template_draft';

  useEffect(() => {
    if (!isEditMode && isInitialized) {
      try {
        const draft = { title, content };
        sessionStorage.setItem(storageKey, JSON.stringify(draft));
      } catch (error) {
        console.error('Failed to save notes template draft to session storage:', error);
      }
    }
  }, [title, content, isEditMode, isInitialized]);

  useEffect(() => {
    if (template) {
      setTitle(template.title || '');
      setContent(template.content || '');
    } else {
      try {
        const saved = sessionStorage.getItem(storageKey);
        if (saved) {
          const draft = JSON.parse(saved);
          if (draft.title) setTitle(draft.title);
          if (draft.content) setContent(draft.content);
        }
      } catch (error) {
        console.error('Failed to load notes template draft from session storage:', error);
      }
    }
    setIsInitialized(true);
  }, [template]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory]);

  const validateForm = () => {
    const newErrors = {};

    if (!title.trim()) {
      newErrors.title = 'Title is required';
    } else if (title.length > TITLE_MAX_LENGTH) {
      newErrors.title = `Title must be ${TITLE_MAX_LENGTH} characters or less`;
    }

    if (!content.trim()) {
      newErrors.content = 'Template content is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    
    const templateData = {
      title: title.trim(),
      content: content.trim()
    };

    onSave(templateData);
    
    if (!isEditMode) {
      try {
        sessionStorage.removeItem(storageKey);
      } catch (error) {
        console.error('Failed to clear notes template draft from session storage:', error);
      }
    }
    
    setIsSaving(false);
  };

  const handleTitleChange = (e) => {
    const newTitle = e.target.value;
    if (newTitle.length <= TITLE_MAX_LENGTH) {
      setTitle(newTitle);
      if (errors.title) {
        setErrors({ ...errors, title: null });
      }
    }
  };

  const handleContentChange = useCallback((newContent) => {
    setContent(newContent);
    if (errors.content) {
      setErrors(prev => ({ ...prev, content: null }));
    }
  }, [errors.content]);

  const handleGenerate = async () => {
    if (!prompt.trim() || isGenerating) return;

    const userPrompt = prompt.trim();
    
    setChatHistory(prev => [...prev, {
      type: 'user',
      content: userPrompt,
      timestamp: Date.now()
    }]);
    
    setPrompt('');
    setIsGenerating(true);
    setPendingContent(null);
    setPendingMessage(null);

    try {
      const response = await consultationService.generateNotesTemplate(userPrompt, content || null);
      
      if (response.error) {
        setChatHistory(prev => [...prev, {
          type: 'error',
          content: response.error,
          timestamp: Date.now()
        }]);
      } else if (response.content) {
        setPendingContent(response.content);
        setPendingMessage(response.message);
      } else {
        setChatHistory(prev => [...prev, {
          type: 'error',
          content: 'No content was generated. Please try a different prompt.',
          timestamp: Date.now()
        }]);
      }
    } catch (err) {
      console.error('Error generating template:', err);
      setChatHistory(prev => [...prev, {
        type: 'error',
        content: 'Failed to generate template. Please try again.',
        timestamp: Date.now()
      }]);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleAcceptPending = () => {
    const contentBefore = content;
    setContent(pendingContent);
    setChatHistory(prev => [...prev, {
      type: 'assistant',
      content: 'Changes accepted',
      message: pendingMessage,
      contentSnapshot: contentBefore,
      timestamp: Date.now()
    }]);
    setPendingContent(null);
    setPendingMessage(null);
  };

  const handleRejectPending = () => {
    setChatHistory(prev => [...prev, {
      type: 'system',
      content: 'Changes rejected',
      timestamp: Date.now()
    }]);
    setPendingContent(null);
    setPendingMessage(null);
  };

  const handleRevert = (contentSnapshot) => {
    setContent(contentSnapshot);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && prompt.trim() && !isGenerating) {
      e.preventDefault();
      handleGenerate();
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div 
        className="modal-container modal-animated notes-template-editor" 
        onClick={(e) => e.stopPropagation()}
        style={{ 
          width: '900px', 
          maxWidth: '95vw',
          height: '80vh',
          maxHeight: '800px',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <div className="modal-header">
          <h2>{isEditMode ? 'Edit Notes Template' : 'Create Notes Template'}</h2>
          <button className="close-button" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        
        <div style={{ padding: '0 20px' }}>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
              Template Title
            </label>
            <input
              type="text"
              value={title}
              onChange={handleTitleChange}
              placeholder="e.g., SOAP Template, Mental Health Assessment..."
              disabled={isSaving}
              maxLength={TITLE_MAX_LENGTH}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '6px',
                border: errors.title ? '1px solid #dc3545' : '1px solid #ddd',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
              <span style={{ color: errors.title ? '#dc3545' : '#999', fontSize: '12px' }}>
                {errors.title || ''}
              </span>
              <span style={{ color: '#999', fontSize: '12px' }}>{title.length} / {TITLE_MAX_LENGTH}</span>
            </div>
          </div>
        </div>

        <div style={{ 
          flex: 1, 
          display: 'flex', 
          gap: '12px', 
          padding: '0 20px 20px 20px',
          minHeight: 0,
          overflow: 'hidden'
        }}>
          {/* Editor Area - shows side by side when pending */}
          <div style={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column',
            minWidth: 0,
            gap: pendingContent ? '8px' : '0'
          }}>
            {/* Side-by-side comparison when pending */}
            {pendingContent ? (
              <>
                {/* Accept/Reject bar */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 12px',
                  backgroundColor: '#6f42c1',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '13px'
                }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <FontAwesomeIcon icon={faWandMagicSparkles} />
                    Review generated changes
                    {pendingMessage && <span style={{ opacity: 0.8, marginLeft: '8px' }}>— {pendingMessage}</span>}
                  </span>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={handleRejectPending}
                      style={{
                        padding: '5px 12px',
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}
                    >
                      Reject
                    </button>
                    <button
                      onClick={handleAcceptPending}
                      style={{
                        padding: '5px 12px',
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}
                    >
                      Accept
                    </button>
                  </div>
                </div>

                {/* Side by side panels */}
                <div style={{ flex: 1, display: 'flex', gap: '8px', minHeight: 0 }}>
                  {/* Current */}
                  <div style={{ 
                    flex: 1, 
                    display: 'flex', 
                    flexDirection: 'column',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    overflow: 'hidden',
                    minWidth: 0
                  }}>
                    <div style={{ 
                      padding: '6px 10px', 
                      backgroundColor: '#f8f9fa', 
                      borderBottom: '1px solid #ddd',
                      fontSize: '12px',
                      color: '#666',
                      fontWeight: '500'
                    }}>
                      Current
                    </div>
                    <TemplateContentField
                      value={content}
                      readOnly
                      flush
                      placeholder="No content"
                    />
                  </div>

                  {/* Generated */}
                  <div style={{ 
                    flex: 1, 
                    display: 'flex', 
                    flexDirection: 'column',
                    border: '2px solid #6f42c1',
                    borderRadius: '6px',
                    overflow: 'hidden',
                    minWidth: 0
                  }}>
                    <div style={{ 
                      padding: '6px 10px', 
                      backgroundColor: 'rgba(111, 66, 193, 0.1)', 
                      borderBottom: '1px solid rgba(111, 66, 193, 0.3)',
                      fontSize: '12px',
                      color: '#6f42c1',
                      fontWeight: '500'
                    }}>
                      Generated
                    </div>
                    <TemplateContentField
                      value={pendingContent}
                      readOnly
                      flush
                      placeholder="No content"
                    />
                  </div>
                </div>
              </>
            ) : (
              /* Normal single editor */
              <div style={{ 
                flex: 1, 
                display: 'flex', 
                flexDirection: 'column',
                minHeight: 0
              }}>
                <div style={{ 
                  padding: '0 0 6px', 
                  fontSize: '12px',
                  color: '#666'
                }}>
                  Template Content
                  {errors.content && <span style={{ color: '#dc3545', marginLeft: '10px' }}>{errors.content}</span>}
                </div>
                <TemplateContentField
                  value={content}
                  onChange={handleContentChange}
                  readOnly={isSaving || isGenerating}
                  placeholder={CONTENT_PLACEHOLDER}
                  hasError={!!errors.content}
                />
              </div>
            )}
          </div>

          {/* Right: Chat Panel */}
          <div style={{ 
            width: '320px',
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid #ddd',
            borderRadius: '8px',
            overflow: 'hidden',
            backgroundColor: '#fafafa'
          }}>
            <div style={{ 
              padding: '10px 14px', 
              backgroundColor: '#6f42c1', 
              color: 'white',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FontAwesomeIcon icon={faWandMagicSparkles} />
                AI Assistant
              </div>
              <button
                onClick={() => setShowHelp(!showHelp)}
                title="Template syntax help"
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '12px'
                }}
              >
                <FontAwesomeIcon icon={faQuestionCircle} />
                Help
                <FontAwesomeIcon icon={showHelp ? faChevronUp : faChevronDown} style={{ fontSize: '10px' }} />
              </button>
            </div>

            {/* Help Panel */}
            {showHelp && (
              <div style={{ 
                padding: '12px',
                backgroundColor: '#fff8e1',
                borderBottom: '1px solid #ffe082',
                fontSize: '12px',
                lineHeight: '1.5'
              }}>
                <div style={{ fontWeight: '600', marginBottom: '8px', color: '#f57c00' }}>
                  Template Syntax Guide
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div>
                    <code style={{ 
                      backgroundColor: '#e3f2fd', 
                      padding: '2px 6px', 
                      borderRadius: '3px',
                      fontFamily: 'monospace',
                      fontSize: '11px'
                    }}>[ description ]</code>
                    <div style={{ marginTop: '2px', color: '#555' }}>
                      <strong>Fill-in section</strong> — The AI will replace this with content from the consultation.
                      <br /><em>Example:</em> <code style={{ fontSize: '10px' }}>[presenting complaint]</code>
                    </div>
                  </div>
                  
                  <div>
                    <code style={{ 
                      backgroundColor: '#e8f5e9', 
                      padding: '2px 6px', 
                      borderRadius: '3px',
                      fontFamily: 'monospace',
                      fontSize: '11px'
                    }}>[? optional section ]</code>
                    <div style={{ marginTop: '2px', color: '#555' }}>
                      <strong>Optional section</strong> — Only appears if relevant. Omitted if not discussed.
                      <br /><em>Example:</em> <code style={{ fontSize: '10px' }}>[? Family history: [relevant details]]</code>
                    </div>
                  </div>
                  
                  <div>
                    <code style={{ 
                      backgroundColor: '#fce4ec', 
                      padding: '2px 6px', 
                      borderRadius: '3px',
                      fontFamily: 'monospace',
                      fontSize: '11px'
                    }}>" fixed text "</code>
                    <div style={{ marginTop: '2px', color: '#555' }}>
                      <strong>Fixed text</strong> — Always included exactly as written.
                      <br /><em>Example:</em> <code style={{ fontSize: '10px' }}>"Reviewed in surgery today."</code>
                    </div>
                  </div>
                  
                  <div>
                    <code style={{ 
                      backgroundColor: '#f3e5f5', 
                      padding: '2px 6px', 
                      borderRadius: '3px',
                      fontFamily: 'monospace',
                      fontSize: '11px'
                    }}>( instruction )</code>
                    <div style={{ marginTop: '2px', color: '#555' }}>
                      <strong>Guidance</strong> — Instructions for how to fill a section (not shown in output).
                      <br /><em>Example:</em> <code style={{ fontSize: '10px' }}>[medications] (only if changes made)</code>
                    </div>
                  </div>
                </div>
                <div style={{ 
                  marginTop: '10px', 
                  paddingTop: '8px', 
                  borderTop: '1px dashed #ffe082',
                  color: '#666',
                  fontSize: '11px'
                }}>
                  <strong>Tip:</strong> Plain text (like headings) appears as-is. You can also write free-form instructions if you prefer not to use syntax.
                </div>
              </div>
            )}

            {/* Chat History */}
            <div style={{ 
              flex: 1, 
              overflow: 'auto', 
              padding: '12px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              {chatHistory.length === 0 && !showHelp && (
                <div style={{ 
                  textAlign: 'center', 
                  color: '#888', 
                  fontSize: '13px',
                  padding: '20px 10px'
                }}>
                  <div>Describe the template you want and I'll help generate it.</div>
                  <div style={{ marginTop: '8px', fontSize: '12px' }}>
                    Click <strong>Help</strong> above for syntax guidance.
                  </div>
                </div>
              )}
              
              {chatHistory.map((msg, idx) => (
                <div key={idx} style={{
                  padding: '8px 10px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  lineHeight: '1.4',
                  ...(msg.type === 'user' ? {
                    backgroundColor: '#e3f2fd',
                    marginLeft: '20px',
                    alignSelf: 'flex-end'
                  } : msg.type === 'error' ? {
                    backgroundColor: 'rgba(220, 53, 69, 0.1)',
                    border: '1px solid rgba(220, 53, 69, 0.3)',
                    color: '#dc3545'
                  } : msg.type === 'system' ? {
                    backgroundColor: '#f8f9fa',
                    color: '#666',
                    textAlign: 'center',
                    fontStyle: 'italic'
                  } : {
                    backgroundColor: 'white',
                    border: '1px solid #e0e0e0',
                    marginRight: '20px'
                  })
                }}>
                  {msg.type === 'user' && (
                    <div style={{ color: '#1976d2' }}>{msg.content}</div>
                  )}
                  
                  {msg.type === 'error' && (
                    <div>{msg.content}</div>
                  )}

                  {msg.type === 'system' && (
                    <div>{msg.content}</div>
                  )}
                  
                  {msg.type === 'assistant' && (
                    <>
                      {msg.message && (
                        <div style={{ 
                          color: '#155724', 
                          marginBottom: '6px',
                          padding: '4px 6px',
                          backgroundColor: 'rgba(40, 167, 69, 0.1)',
                          borderRadius: '4px',
                          fontSize: '11px'
                        }}>
                          {msg.message}
                        </div>
                      )}
                      <div style={{ color: '#28a745', marginBottom: '6px' }}>
                        Changes accepted
                      </div>
                      {msg.contentSnapshot !== undefined && (
                        <button
                          onClick={() => handleRevert(msg.contentSnapshot)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '3px 6px',
                            fontSize: '11px',
                            color: '#6c757d',
                            backgroundColor: 'transparent',
                            border: '1px solid #dee2e6',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          <FontAwesomeIcon icon={faRotateLeft} />
                          Revert
                        </button>
                      )}
                    </>
                  )}
                </div>
              ))}
              
              {isGenerating && (
                <div style={{
                  padding: '10px 12px',
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  border: '1px solid #e0e0e0',
                  marginRight: '20px',
                  fontSize: '13px',
                  color: '#6f42c1',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  Generating...
                </div>
              )}
              
              <div ref={chatEndRef} />
            </div>

            {/* Prompt Input */}
            <div style={{ 
              padding: '12px',
              borderTop: '1px solid #ddd',
              backgroundColor: 'white'
            }}>
              <div style={{ 
                display: 'flex', 
                gap: '8px',
                alignItems: 'flex-end'
              }}>
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Describe your template..."
                  disabled={isGenerating || isSaving}
                  rows={2}
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: '1px solid #ddd',
                    fontSize: '13px',
                    resize: 'none',
                    fontFamily: 'inherit'
                  }}
                />
                <button
                  onClick={handleGenerate}
                  disabled={!prompt.trim() || isGenerating || isSaving}
                  style={{
                    padding: '10px 14px',
                    backgroundColor: isGenerating ? '#6c757d' : '#6f42c1',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: !prompt.trim() || isGenerating ? 'not-allowed' : 'pointer',
                    opacity: !prompt.trim() ? 0.6 : 1
                  }}
                >
                  <FontAwesomeIcon icon={isGenerating ? faSpinner : faPaperPlane} spin={isGenerating} />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="modal-actions" style={{ borderTop: '1px solid #eee', padding: '16px 20px' }}>
          <button className="cancel-button" onClick={onClose} disabled={isSaving}>
            Cancel
          </button>
          
          <button 
            className="submit-button" 
            onClick={handleSave}
            disabled={isSaving || !title.trim() || !content.trim()}
          >
            {isSaving ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="fa-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <span>{isEditMode ? 'Update' : 'Create'} Template</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(NotesTemplateEditorModal);
